// frontend/src/components/cohesion/UndoToast.tsx
// Brighella v2 (ADR-014 D12) — Floating toast with inline "Undo" CTA.
//
// Reads useUndoStore.latest; when present, renders a fixed bottom-center
// toast counting down to `reversible_until`. Clicking Undo (or Cmd+Z)
// triggers POST /api/revisions/:id/undo via useUndo. Toast auto-dismisses
// when the window expires.

import { useEffect, useState } from 'react'
import { Undo2 } from 'lucide-react'
import { useUndoStore } from '../../store/undoStore'
import { useUndo } from '../../hooks/useUndo'
import { useLanguage } from '../../i18n/index.jsx'
import './UndoToast.css'

export default function UndoToast() {
  const latest = useUndoStore((s) => s.latest)
  const clear  = useUndoStore((s) => s.clear)
  const { t } = useLanguage()
  const { undoNow } = useUndo({
    onSuccess: () => {
      // Latest already cleared by popLatest; nothing more to do.
    },
  })

  const [remaining, setRemaining] = useState(0)

  useEffect(() => {
    if (!latest) return
    const tick = () => {
      const expiresAt = new Date(latest.reversible_until).valueOf()
      const ms = Math.max(0, expiresAt - Date.now())
      setRemaining(ms)
      if (ms <= 0) clear()
    }
    tick()
    const id = setInterval(tick, 250)
    return () => clearInterval(id)
  }, [latest, clear])

  if (!latest) return null
  if (remaining <= 0) return null

  const seconds = Math.ceil(remaining / 1000)

  return (
    <div role="status" aria-live="polite" className="undo-toast">
      <span className="undo-toast-label">
        {latest.label || t('undoToastDefaultLabel')}
      </span>
      <button
        type="button"
        className="undo-toast-button"
        onClick={undoNow}
        aria-label={t('undo')}
      >
        <Undo2 size={14} aria-hidden="true" />
        {t('undo')}
        <kbd className="undo-toast-kbd">⌘Z</kbd>
      </button>
      <span className="undo-toast-countdown" aria-hidden="true">{seconds}s</span>
    </div>
  )
}
